import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { ApplyPunchPassModalContext } from "../../../atoms";
import { BookingPunchPassContent } from "./BookingPunchPassContent";
import { RoundPunchPassContent } from "./RoundPunchPassContent";
import {
  ApplyPunchPassModalContent_Booking$key,
  ApplyPunchPassModalContent_Booking$data,
} from "./__generated__/ApplyPunchPassModalContent_Booking.graphql";
import { RoundPunchPassContent_Round$key } from "./__generated__/RoundPunchPassContent_Round.graphql";

export type ApplyPunchPassModalContentProps = {
  bookingRef: ApplyPunchPassModalContent_Booking$key;
  context: ApplyPunchPassModalContext | null;
  roundApplied: { rateId: string | null; transportId: string | null } | null;
  bookingAppliedId: string | null;
  onRoundAppliedChange: (
    ratePunchPassId: string | null,
    transportPunchPassId: string | null
  ) => void;
  onBookingAppliedChange: (punchPassId: string | null) => void;
};

function getRoundRefFromBooking(
  booking: ApplyPunchPassModalContent_Booking$data,
  roundId: string
): RoundPunchPassContent_Round$key | null {
  const edges = booking.rounds?.edges ?? [];
  for (const edge of edges) {
    if (edge?.node?.id === roundId) {
      return edge.node as RoundPunchPassContent_Round$key;
    }
  }
  return null;
}

export const ApplyPunchPassModalContent = ({
  bookingRef,
  context,
  roundApplied,
  bookingAppliedId,
  onRoundAppliedChange,
  onBookingAppliedChange,
}: ApplyPunchPassModalContentProps) => {
  const booking = useFragment(ApplyPunchPassModalContentFragment, bookingRef);

  if (context?.type === "round") {
    const roundRef = getRoundRefFromBooking(booking, context.roundId);
    if (!roundRef) return null;
    const applied = roundApplied ?? { rateId: null, transportId: null };
    return (
      <RoundPunchPassContent
        roundRef={roundRef}
        appliedRateId={applied.rateId}
        appliedTransportId={applied.transportId}
        onAppliedChange={onRoundAppliedChange}
      />
    );
  }

  if (context?.type === "booking") {
    return (
      <BookingPunchPassContent
        bookingRef={booking}
        appliedId={bookingAppliedId}
        onAppliedChange={onBookingAppliedChange}
      />
    );
  }

  return null;
};

export const ApplyPunchPassModalContentFragment = graphql`
  fragment ApplyPunchPassModalContent_Booking on Booking {
    id
    ownerClient {
      ... on Member {
        fullName
      }
      ... on Customer {
        fullName
      }
    }
    rounds {
      edges {
        node {
          id
          ratePunchPass {
            id
          }
          transportationPunchPass {
            id
          }
          ...RoundPunchPassContent_Round
        }
      }
    }
    ... on StandardBooking {
      appliedPunchPass {
        id
      }
    }
    ... on LessonBooking {
      appliedPunchPass {
        id
      }
    }
    ...BookingPunchPassContent_Booking
  }
`;
